import {
  useGetInvoicesSuspense,
  useGetUsersSuspense,
} from 'api/payments/generated/fragmentPaymentsAPI';
import { PaymentViewLayout } from 'components/Layout/PaymentViewLayout';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import React, { Suspense, useMemo, useState } from 'react';
import { makePath } from 'utils/routes';
import { useLocation } from 'wouter';

import { PayoutModal } from './PayoutModal';
import { UserView } from './UserView';

type Props = {
  workspaceId: string;
  externalId: string;
};

const UserDetailPageContent = ({
  workspaceId,
  externalId,
}: Props): JSX.Element => {
  useDocumentTitle('User Details');
  const [showPayoutModal, setShowPayoutModal] = useState(false);

  const { data: usersData } = useGetUsersSuspense();
  const { data: invoicesData } = useGetInvoicesSuspense();
  const [, setLocation] = useLocation();

  // Find the specific user by externalId
  const user = useMemo(
    () => usersData.data.find((u) => u.externalId === externalId),
    [usersData, externalId]
  );

  // Find all invoices that include this user
  const userInvoices = useMemo(
    () =>
      invoicesData.data.filter((invoice) => {
        // Check if user is a seller in any line item
        if (!invoice.lineItems || invoice.lineItems.length === 0) return false;
        return invoice.lineItems.some((item) => item.user_id === externalId);
      }),
    [invoicesData, externalId]
  );

  const handleBackToUsers = () => {
    setLocation(
      makePath(['w', workspaceId, 'payments', 'users'], { absolute: true })
    );
  };

  if (!user) {
    return (
      <PaymentViewLayout title="User Details">
        <div className="text-red-500 text-medium">
          User not found: {externalId}
        </div>
      </PaymentViewLayout>
    );
  }

  const handlePayoutClick = () => {
    setShowPayoutModal(true);
  };

  return (
    <>
      <PaymentViewLayout
        title="User Details"
        onBack={handleBackToUsers}
        primaryAction={{
          title: 'Payout',
          onClick: handlePayoutClick,
          'data-testid': 'payout-button',
        }}
        backTitle="Users"
      >
        <UserView
          user={user}
          userInvoices={userInvoices}
          externalId={externalId}
          workspaceId={workspaceId}
        />
      </PaymentViewLayout>
      {showPayoutModal && (
        <PayoutModal
          onClose={() => setShowPayoutModal(false)}
          invoices={userInvoices
            .filter((invoice) => invoice.status === 'active')
            .map((invoice) => {
              // Calculate total amount from line items for this user
              const lineItems = invoice.lineItems || [];
              const userLineItems = lineItems.filter(
                (item) => item.user_id === externalId
              );
              const totalAmount = userLineItems.reduce(
                (sum: number, item) => sum + Number(item.amount),
                0
              );

              return {
                id: invoice.id,
                amount: totalAmount.toString(),
                currency: 'USD',
                status: invoice.status,
                createdAt: invoice.created,
              };
            })}
          userName={externalId}
        />
      )}
    </>
  );
};

export const UserDetailPage = ({
  workspaceId,
  externalId,
}: Props): JSX.Element => (
  <Suspense
    fallback={
      <PaymentViewLayout title="User Details">
        <div className="text-main-500 text-medium">Loading user...</div>
      </PaymentViewLayout>
    }
  >
    <UserDetailPageContent workspaceId={workspaceId} externalId={externalId} />
  </Suspense>
);
